import type { CatalogApi } from '@backstage/catalog-client';
import type { Entity } from '@backstage/catalog-model';
import { DEFAULT_NAMESPACE } from '@backstage/catalog-model';

export const MY_GROUPS_PATH = '/my-groups';

const POLL_MS = 5_000;

function sortGroups(groups: Entity[]): Entity[] {
  return [...groups].sort((a, b) =>
    (a.metadata.name ?? '').localeCompare(b.metadata.name ?? ''),
  );
}

/** Direct GitLab group memberships from the signed-in user's catalog User entity. */
export async function fetchMyGroups(
  catalogApi: CatalogApi,
  userEntityRef: string,
): Promise<{
  groups: Entity[];
  memberOfCount: number;
  /** `spec.memberOf` names that are not yet resolvable as Group entities in the catalog. */
  pendingGroupNames: string[];
}> {
  const user = await catalogApi.getEntityByRef(userEntityRef);
  const memberOf = (user?.spec?.memberOf as string[] | undefined) ?? [];

  if (memberOf.length === 0) {
    return { groups: [], memberOfCount: 0, pendingGroupNames: [] };
  }

  const resolved = await Promise.all(
    memberOf.map(name =>
      catalogApi.getEntityByRef({
        kind: 'group',
        namespace: DEFAULT_NAMESPACE,
        name,
      }),
    ),
  );

  const groups = sortGroups(resolved.filter((g): g is Entity => Boolean(g)));
  const pendingSet = new Set(
    memberOf
      .map((name, i) => (!resolved[i] && name?.trim() ? name.trim() : null))
      .filter((x): x is string => Boolean(x)),
  );
  const pendingGroupNames = [...pendingSet].sort((a, b) => a.localeCompare(b));

  return {
    groups,
    memberOfCount: memberOf.length,
    pendingGroupNames,
  };
}

/** Poll briefly while direct memberships may still be publishing after login. */
export function myGroupsPollIntervalMs(options: {
  groupCount: number;
  memberOfCount: number;
}): number | undefined {
  const { groupCount, memberOfCount } = options;
  if (memberOfCount > groupCount) {
    return POLL_MS;
  }
  if (groupCount === 0) {
    return POLL_MS;
  }
  return undefined;
}
